export interface ModelConfig {
  id: ModelId;
  provider: string;
  modelName: string;
  displayName: string;
  reasoningEffort?: ReasoningEffort;
  supportedReasoningEfforts?: readonly ReasoningEffort[];
  temperature?: number;
  maxOutputTokens: number;
  contextWindow: number;
  contextSummarizationThreshold?: number;
}

export type ReasoningEffort =
  | "none"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max";

export const REASONING_EFFORTS: readonly ReasoningEffort[] = [
  "none",
  "low",
  "medium",
  "high",
  "xhigh",
];

export enum ModelId {
  GPT_6_LUNA = "gpt-6-luna",
  GPT_6_ASTRA = "gpt-6-astra",
  GPT_6_1_SOL = "gpt-6.1-sol",
  GPT_6_1_TERRA = "gpt-6.1-terra",
  GROK_4_6 = "grok-4.6",
  CLAUDE_OPUS_5_5 = "claude-opus-5-5",
  CLAUDE_SONNET_5_5 = "claude-sonnet-5-5",
  GEMINI_3_8_FLASH = "gemini-3.8-flash",
  DEEPSEEK_V4_FLASH = "deepseek-v4-flash-0731",
  DEEPSEEK_V4_1_FLASH = "deepseek-v4.1-flash",
  ZAI_GLM_5_2 = "zai-glm-5.2",
}

const GPT_6_REASONING_EFFORTS: readonly ReasoningEffort[] = [
  "none",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

const GPT_6_NO_NONE_REASONING_EFFORTS: readonly ReasoningEffort[] = [
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

export const AVAILABLE_MODELS: Record<ModelId, ModelConfig> = {
  [ModelId.GPT_6_1_SOL]: {
    id: ModelId.GPT_6_1_SOL,
    provider: "openai",
    reasoningEffort: "high",
    supportedReasoningEfforts: GPT_6_NO_NONE_REASONING_EFFORTS,
    modelName: "gpt-6.1-sol",
    displayName: "GPT-6.1 Sol",
    maxOutputTokens: 128000,
    contextWindow: 1050000,
  },
  [ModelId.GPT_6_1_TERRA]: {
    id: ModelId.GPT_6_1_TERRA,
    provider: "openai",
    reasoningEffort: "medium",
    supportedReasoningEfforts: GPT_6_REASONING_EFFORTS,
    modelName: "gpt-6.1-terra",
    displayName: "GPT-6.1 Terra",
    maxOutputTokens: 128000,
    contextWindow: 1050000,
  },
  [ModelId.GPT_6_LUNA]: {
    id: ModelId.GPT_6_LUNA,
    provider: "openai",
    reasoningEffort: "medium",
    supportedReasoningEfforts: GPT_6_REASONING_EFFORTS,
    modelName: "gpt-6-luna",
    displayName: "GPT-6 Luna",
    maxOutputTokens: 128000,
    contextWindow: 1050000,
  },
  [ModelId.GPT_6_ASTRA]: {
    id: ModelId.GPT_6_ASTRA,
    provider: "openai",
    reasoningEffort: "medium",
    supportedReasoningEfforts: GPT_6_NO_NONE_REASONING_EFFORTS,
    modelName: "gpt-6-astra",
    displayName: "GPT-6 Astra",
    maxOutputTokens: 128000,
    contextWindow: 1050000,
  },
  [ModelId.GROK_4_6]: {
    id: ModelId.GROK_4_6,
    provider: "xai",
    modelName: "grok-4.6",
    displayName: "Grok 4.6",
    reasoningEffort: "high",
    supportedReasoningEfforts: ["low", "medium", "high", "xhigh"],
    temperature: 1,
    contextWindow: 500000,
    maxOutputTokens: 128000,
  },
  [ModelId.CLAUDE_OPUS_5_5]: {
    id: ModelId.CLAUDE_OPUS_5_5,
    provider: "anthropic",
    modelName: "claude-opus-5-5",
    displayName: "Claude Opus 5.5",
    reasoningEffort: "medium",
    supportedReasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
    maxOutputTokens: 128000,
    contextWindow: 1000000,
    contextSummarizationThreshold: 400000,
  },
  [ModelId.CLAUDE_SONNET_5_5]: {
    id: ModelId.CLAUDE_SONNET_5_5,
    provider: "anthropic",
    modelName: "claude-sonnet-5-5",
    displayName: "Claude Sonnet 5.5",
    reasoningEffort: "high",
    supportedReasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
    maxOutputTokens: 128000,
    contextWindow: 1000000,
    contextSummarizationThreshold: 400000,
  },
  [ModelId.GEMINI_3_8_FLASH]: {
    id: ModelId.GEMINI_3_8_FLASH,
    provider: "google_genai",
    modelName: "gemini-3.8-flash",
    displayName: "Gemini 3.8 Flash",
    reasoningEffort: "medium",
    supportedReasoningEfforts: ["low", "medium", "high"],
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    contextSummarizationThreshold: 900000,
  },
  [ModelId.DEEPSEEK_V4_FLASH]: {
    id: ModelId.DEEPSEEK_V4_FLASH,
    provider: "openrouter",
    modelName: "deepseek/deepseek-v4-flash-0731",
    displayName: "DeepSeek V4 Flash",
    contextWindow: 1048576,
    maxOutputTokens: 128000,
    contextSummarizationThreshold: 900000,
  },
  [ModelId.DEEPSEEK_V4_1_FLASH]: {
    id: ModelId.DEEPSEEK_V4_1_FLASH,
    provider: "openrouter",
    modelName: "deepseek/deepseek-v4.1-flash",
    displayName: "DeepSeek V4.1 Flash",
    contextWindow: 1048576,
    maxOutputTokens: 128000,
    contextSummarizationThreshold: 900000,
  },
  [ModelId.ZAI_GLM_5_2]: {
    id: ModelId.ZAI_GLM_5_2,
    provider: "openrouter",
    modelName: "z-ai/glm-5.2",
    displayName: "ZAI GLM 5.2",
    reasoningEffort: "high",
    supportedReasoningEfforts: ["high", "xhigh"],
    temperature: 1.0,
    contextWindow: 1000000,
    maxOutputTokens: 128000,
  },
  // [ModelId.KIMI_K2_INSTRUCT]: {
  //   id: ModelId.KIMI_K2_INSTRUCT,
  //   provider: "groq",
  //   modelName: "moonshotai/kimi-k2-instruct",
  //   displayName: "Kimi K2",
  //   temperature: 0.6,
  //   contextWindow: 128000,
  //   maxOutputTokens: 16000,
  // },
  // [ModelId.GPT_OSS_120B]: {
  //   id: ModelId.GPT_OSS_120B,
  //   provider: "groq",
  //   modelName: "openai/gpt-oss-120b",
  //   displayName: "GPT OSS 120B",
  //   temperature: 0.6,
  //   contextWindow: 131072,
  //   maxOutputTokens: 65536,
  // },
  // [ModelId.GLM_4_6]: {
  //   id: ModelId.GLM_4_6,
  //   provider: "openrouter",
  //   modelName: "z-ai/glm-4.6",
  //   displayName: "GLM 4.6",
  //   temperature: 0.8,
  //   contextWindow: 131072, // 128k total context window
  //   maxOutputTokens: 64000, // Conservative limit - total must fit in 128k with input+tools
  // },
};

export const AGENT_RECURSION_LIMIT = 200;

const MODEL_ID_SET: ReadonlySet<string> = new Set(Object.values(ModelId));
const LEGACY_MODEL_ID_MIGRATIONS: Readonly<Record<string, ModelId>> = {
  "gpt-5.5": ModelId.GPT_6_1_SOL,
  "gpt-5.6-sol": ModelId.GPT_6_1_SOL,
  "gpt-5.6-terra": ModelId.GPT_6_1_SOL,
  "gpt-5.6-luna": ModelId.GPT_6_LUNA,
  "claude-opus-4-8": ModelId.CLAUDE_OPUS_5_5,
  "claude-opus-5": ModelId.CLAUDE_OPUS_5_5,
  "gemini-3.1-pro-preview": ModelId.GEMINI_3_8_FLASH,
  "gemini-3-flash-preview": ModelId.GEMINI_3_8_FLASH,
  "gemini-3.5-flash": ModelId.GEMINI_3_8_FLASH,
};

export function isModelId(value: string): value is ModelId {
  return MODEL_ID_SET.has(value);
}

export function resolveStoredModelId(value: string): ModelId | undefined {
  if (isModelId(value)) {
    return value;
  }

  return LEGACY_MODEL_ID_MIGRATIONS[value];
}

export function normalizeReasoningEffort(
  modelConfig: ModelConfig,
  reasoningEffort?: ReasoningEffort
): ReasoningEffort | undefined {
  const defaultReasoningEffort = modelConfig.reasoningEffort;
  if (!defaultReasoningEffort) {
    return undefined;
  }

  const supportedReasoningEfforts =
    modelConfig.supportedReasoningEfforts ?? REASONING_EFFORTS;
  return reasoningEffort && supportedReasoningEfforts.includes(reasoningEffort)
    ? reasoningEffort
    : defaultReasoningEffort;
}

export const DEFAULT_MODEL_ID = ModelId.GPT_6_1_SOL;
